/**
 * CREATIVE DETONATOR - Supabase Auth Client
 *
 * Initializes Supabase client for authentication.
 * Configuration is loaded from window.AUTH_CONFIG or defaults.
 */

import { createClient } from '@supabase/supabase-js';

// ============================================================================
// CONFIGURATION
// ============================================================================

// Config can be set globally before this module loads, or will use defaults
const config = window.AUTH_CONFIG || {
  supabaseUrl: 'YOUR_SUPABASE_URL',
  supabaseAnonKey: 'YOUR_SUPABASE_ANON_KEY'
};

// Check if configuration is valid
const isConfigured = config.supabaseUrl !== 'YOUR_SUPABASE_URL' &&
                     config.supabaseUrl?.startsWith('http');

// Validate configuration
if (!isConfigured) {
  console.warn('[Auth] Supabase not configured. Set window.AUTH_CONFIG before loading auth modules.');
}

// ============================================================================
// SUPABASE CLIENT
// ============================================================================

// Only create client if properly configured
export const supabase = isConfigured
  ? createClient(config.supabaseUrl, config.supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      }
    })
  : null;

// Expose globally for non-module scripts (e.g., brand-genesis.js)
if (supabase) {
  window.supabase = supabase;
}

// ============================================================================
// AUTH HELPERS
// ============================================================================

/**
 * Get current session access token
 * @returns {Promise<string|null>} - JWT token or null
 */
export async function getSessionToken() {
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Force refresh the session token
 * Call this when a 401 is received to attempt token renewal
 * @returns {Promise<string|null>} - New JWT token or null
 */
export async function refreshSessionToken() {
  if (!supabase) return null;
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    if (error) {
      console.warn('[Auth] Token refresh failed:', error.message);
      return null;
    }
    return session?.access_token || null;
  } catch (e) {
    console.warn('[Auth] Token refresh error:', e.message);
    return null;
  }
}

/**
 * Get current authenticated user
 * @returns {Promise<Object|null>} - User object or null
 */
export async function getCurrentUser() {
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Check if user is currently logged in
 * @returns {Promise<boolean>}
 */
export async function isLoggedIn() {
  if (!supabase) return false;
  const user = await getCurrentUser();
  return !!user;
}

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Called with (event, session) on auth changes
 * @returns {Object} - Subscription object with unsubscribe method
 */
export function onAuthChange(callback) {
  if (!supabase) {
    return { data: { subscription: { unsubscribe: () => {} } } };
  }
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
}

/**
 * Sign out the current user
 * @returns {Promise<{error: Error|null}>}
 */
export async function signOut() {
  if (!supabase) return { error: new Error('Auth not configured') };
  const { error } = await supabase.auth.signOut();
  return { error };
}

/**
 * Get current session
 * @returns {Promise<Object|null>} - Session object or null
 */
export async function getSession() {
  if (!supabase) return null;
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Check if auth is configured
 * @returns {boolean}
 */
export function isAuthConfigured() {
  return isConfigured;
}

// ============================================================================
// EXPORTS
// ============================================================================

// Export config for reference
export { config as authConfig };
